import React from "react";
import styled from "styled-components";
import { Form, Formik, FormikHelpers } from "formik";
import * as Sentry from "@sentry/react";
import * as Yup from "yup";
import Employer from "../../apiResources/Employer";
import EmployerUser from "../../apiResources/EmployerUser";
import TextLink from "../library/TextLink";
import { TextInputField } from "../library/Form";
import SubmitButton from "../library/Form/SubmitButton";
import SettingLabel from "./SettingLabel";

const NumberOfEmployeesSettingsOption = (props: Props) => {
  const {
    user,
    company,
    isEditing = false,
    disabled = false,
    onEditStart,
    onEditComplete,
  } = props;

  const save = async (
    values: FormValues,
    formik: FormikHelpers<FormValues>
  ) => {
    if (values.numberOfEmployees === company.numberOfEmployees) {
      onEditComplete();
      return;
    }

    try {
      company.assignAttributes(values);
      const success = await company.save();
      if (success) {
        onEditComplete();
      } else {
        formik.setErrors({
          numberOfEmployees: company.errors.numberOfEmployees?.fullMessage,
        });
      }
    } catch (err) {
      Sentry.captureException(err);
      formik.setErrors({
        numberOfEmployees: "Uh oh, something went wrong there",
      });
    } finally {
      company.rollback();
    }
  };

  const edit = () => {
    if (!disabled) {
      onEditStart();
    }
  };

  if (!isEditing) {
    return (
      <section>
        <Header>
          <SettingLabel>Number of Employees</SettingLabel>
          {user.isAdmin && (
            <TextLink disabled={disabled} onClick={edit}>
              Edit
            </TextLink>
          )}
        </Header>
        {company.numberOfEmployees ? (
          <div>{company.numberOfEmployees}</div>
        ) : (
          <div>Not specified</div>
        )}
      </section>
    );
  }

  const initialValues: FormValues = {
    numberOfEmployees: company.numberOfEmployees,
  };

  const validation = Yup.object({
    name: Yup.string(),
  });

  return (
    <section>
      <Formik
        initialValues={initialValues}
        onSubmit={save}
        validationSchema={validation}
      >
        <Form autoComplete="off">
          <Header>
            <SettingLabel>Number of Employees</SettingLabel>
            <TextLink onClick={onEditComplete}>Cancel</TextLink>
          </Header>
          <TextInputField
            label="Number of Employees"
            hiddenLabel
            name="numberOfEmployees"
          />
          <SubmitButton />
        </Form>
      </Formik>
    </section>
  );
};

type FormValues = {
  numberOfEmployees: number;
};

type Props = {
  user: EmployerUser;
  company: Employer;
  isEditing?: boolean;
  editable?: boolean;
  disabled?: boolean;
  onEditStart: () => void;
  onEditComplete: () => void;
};

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

export default NumberOfEmployeesSettingsOption;
